import React, { useState } from 'react';
import HorseCard from './HorseCard';
import SortingControls from './SortingControls';
import PDFGenerator from '../PDFGenerator/index';

const SavedHorses = ({ 
  savedHorses, 
  onEditHorse, 
  onDeleteHorse,
  onCloneHorse,
  onSendSummarySms,
  colors,
  commonStyles,
  setError,
  horseReminderEnabledMap,
  onToggleHorseReminder,
}) => {
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSortChange = (e) => {
    const [field, order] = e.target.value.split('-');
    setSortBy(field);
    setSortOrder(order);
  };

  // Sort horses locally without modifying the parent state
  const getSortedHorses = () => {
    if (!savedHorses) return [];
    
    return [...savedHorses].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'horse_name':
          comparison = (a.horse_name || '').localeCompare(b.horse_name || '');
          break;
        case 'class_name':
          comparison = (a.class_name || '').localeCompare(b.class_name || '');
          break;
        case 'draw_number':
          comparison = (parseInt(a.draw_number) || 0) - (parseInt(b.draw_number) || 0);
          break;
        case 'estimated_run_time':
          const timeA = a.estimated_run_time ? new Date('1970/01/01 ' + a.estimated_run_time).getTime() : 0;
          const timeB = b.estimated_run_time ? new Date('1970/01/01 ' + b.estimated_run_time).getTime() : 0;
          comparison = timeA - timeB;
          break;
        default:
          comparison = new Date(a.created_at) - new Date(b.created_at);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  // Early return for loading state
  if (!savedHorses) {
    return (
      <div style={{...commonStyles.section}}>
        <h2 style={{
          color: colors.primary,
          fontSize: '1.75rem',
          fontWeight: '600',
          marginBottom: '24px'
        }}>
          Saved Horses
        </h2>
        <div style={{
          textAlign: 'center',
          color: colors.text.secondary,
          padding: '24px'
        }}>
          Loading horses...
        </div>
      </div>
    );
  }

  // Return for empty state
  if (!Array.isArray(savedHorses) || savedHorses.length === 0) {
    return (
      <div style={{...commonStyles.section}}>
        <h2 style={{
          color: colors.primary,
          fontSize: '1.75rem',
          fontWeight: '600',
          marginBottom: '24px'
        }}>
          Saved Horses
        </h2>
        <div style={{
          textAlign: 'center',
          color: colors.text.secondary,
          padding: '24px'
        }}>
          No saved horses yet
        </div>
      </div>
    );
  }

  const sortedHorses = getSortedHorses();

  return (
    <div style={{ width: '100%' }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <h3 style={{
          fontSize: '1.2rem',
          fontWeight: '700',
          color: colors.text.primary,
          margin: 0
        }}>
          Saved Horses ({savedHorses.length})
        </h3>
        {savedHorses.length > 1 && (
          <SortingControls onSortChange={handleSortChange} />
        )}
      </div>

      <div style={{
        backgroundColor: colors.background,
        padding: '12px',
        borderRadius: '8px',
        marginBottom: '16px',
        fontSize: '1rem',
        color: colors.text.secondary
      }}>
        Use the 'Clone' feature to duplicate a horse competing multiple times in a weekend. Update the name to reflect each day, e.g., 'XYZ Saturday' → 'XYZ Sunday,' and adjust race details as needed.
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        maxHeight: '600px',
        overflowY: 'auto',
        overflowX: 'visible', // Allow tooltips to overflow horizontally
        padding: '48px 4px 4px 4px' // Extra padding at top for tooltips on first card
      }}>
        {sortedHorses.map((horse) => (
          <HorseCard
            key={horse.id}
            horse={horse}
            onEdit={() => onEditHorse(horse)}
            onDelete={() => onDeleteHorse(horse.id)}
            onClone={() => onCloneHorse(horse)}
            onSendSummary={() => onSendSummarySms && onSendSummarySms(horse)}
            colors={colors}
            commonStyles={commonStyles}
            reminderEnabled={!!horseReminderEnabledMap?.[horse.id]}
            onToggleReminder={() => onToggleHorseReminder && onToggleHorseReminder(horse.id)}
          />
        ))}
      </div>

      {savedHorses.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '24px'
        }}>
          <PDFGenerator
            savedHorses={savedHorses}
            setError={setError}
          />
        </div>
      )}
    </div>
  );
};

export default SavedHorses;
